import type { Layout } from '../../data/types';
import type { Vec2 } from '../../geo/types';
import { planFrameOf, toLayout } from '../fabric/landmarks/planFrame';
import { leprozenhuisPlan } from '../fabric/landmarks/leprozenhuis';
import { travel, heading } from './itinerary';
import { routineDuration } from './routinePose';
import type { LifeClearance } from './routes';
import { routeClear } from './routes';
import type { LifeRoute, LifeStep } from './types';

/** Everyday work is dramatized in the existing courts, without claiming recorded staff identities. */
export function gasthuisRoutines(layout: Layout, clearance: LifeClearance): LifeRoute[] {
  const routes: LifeRoute[] = [];
  const pieters = layout.landmarks.find((l) => l.id === 'landmark-sint-pietersgasthuis');
  if (pieters) {
    const frame = planFrameOf(pieters.footprint, pieters.heading);
    const jobs = [
      {
        id: 'bread',
        woman: true,
        points: [
          [-8.8, 5.8],
          [-4.5, 5.8],
          [-4.5, -1.4],
          [-8.8, -1.4],
        ],
        face: [-8.8, 7.2],
      },
      {
        id: 'linen',
        woman: true,
        points: [
          [8.8, -1.2],
          [5, -1.2],
          [5, 8.2],
          [11.5, 8.2],
          [11.5, -1.2],
        ],
        face: [8.8, 0.4],
      },
      {
        id: 'supplies',
        woman: false,
        points: [
          [-6.2, 4.1],
          [-2.2, 4.1],
          [2.2, 2.8],
          [6.2, 2.8],
          [6.2, 5.8],
          [2, 6.8],
          [-2.2, 6.8],
          [-6.2, 6.8],
        ],
        face: [-8.8, 7.2],
      },
    ] as const;
    for (const [i, job] of jobs.entries())
      routes.push(
        chore(
          `gasthuis-${job.id}`,
          pieters.id,
          job.points.map((p) => toLayout(frame, p)),
          toLayout(frame, job.face),
          job.woman,
          i,
          clearance
        )
      );
  }
  const leprozen = layout.landmarks.find((l) => l.id === 'landmark-leprozenhuis');
  if (leprozen) {
    const plan = leprozenhuisPlan(leprozen);
    const at = (p: Vec2): Vec2 =>
      toLayout(plan.plan, [plan.centre[0] + p[0] * plan.scale, plan.centre[1] + p[1] * plan.scale]);
    routes.push(
      chore(
        'gasthuis-leprozen-care',
        leprozen.id,
        (
          [
            [-11.5, -3.5],
            [7, -3.5],
            [7, 0.5],
            [-11.5, 0.5],
          ] as const
        ).map(at),
        at([-11.5, -5.5]),
        true,
        3,
        clearance
      )
    );
  }
  return routes;
}

function chore(
  id: string,
  streetId: string,
  points: readonly Vec2[],
  face: Vec2,
  woman: boolean,
  index: number,
  clearance: LifeClearance
): LifeRoute {
  const steps: LifeStep[] = [];
  const speed = 0.82 + index * 0.025;
  const stop = (at: Vec2, seconds: number, toward: Vec2) =>
    steps.push({
      from: at,
      to: at,
      seconds,
      action: 'work',
      heading: heading(at, toward),
      prop: 'basket',
    });
  stop(points[0]!, 7 + index * 2, face);
  for (let i = 1; i <= points.length; i++) {
    travel(steps, [points[i - 1]!, points[i % points.length]!], speed, 'basket');
    if (i === Math.floor(points.length / 2)) stop(points[i]!, 5 + index, points[i - 1]!);
  }
  for (const step of steps)
    if (!routeClear({ a: step.from, b: step.to, radius: 0.48 }, clearance))
      throw new Error(`Blocked gasthuis chore: ${id}`);
  const route: LifeRoute = {
    id,
    role: 'carer',
    streetId,
    a: points[0]!,
    b: points[Math.floor(points.length / 2)]!,
    radius: 0.48,
    speed,
    scale: 0.97 + index * 0.01,
    phase: 0,
    ...(woman ? { appearance: 'woman' as const } : {}),
    daytime: true,
    routine: steps,
  };
  return { ...route, phase: (routineDuration(route) * index) / 4 };
}
